import { Drawer, Table } from '@/components/commons';
import { ENDPOINT } from '@/services/endpoint';
import { ImageService } from '@/services/image';
import { IllustrationThemeService } from '@/services/illustrationTheme';
import { IResponse, IResponseError } from '@/types/common';
import {
  ICreateIllustrationThemeRequest,
  IUpdateIllustrationThemeRequest,
} from '@/types/request/illustrationTheme';
import { IGetDetailIllustrationThemeResponse } from '@/types/response/illustrationTheme';
import {
  handleErrorCommon,
  notificationMessage,
  notificationModal,
  NOTIFY_STATUS,
} from '@/utils';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Image, Tag, TableColumnsType } from 'antd';
import moment from 'moment';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { FormProvider, useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  CoverImage,
  IllustrationThemeForm,
} from '../Drawers/IllustrationThemeForm';
import { formSchemaFn } from '../Drawers/IllustrationThemeForm/formSchemaFn';
import { PreviewSamplePanel } from '../PreviewSamplePanel';

interface IProps {
  title?: string;
}

type FormValues = ICreateIllustrationThemeRequest & {
  coverImage?: CoverImage | null;
};

export const IllustrationThemeList: React.FC<IProps> = ({ title }) => {
  const queryClient = useQueryClient();
  const [currentId, setCurrentId] = useState<number | null>(null);
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [detail, setDetail] = useState<IGetDetailIllustrationThemeResponse>();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const { data, refetch, isFetching } = useQuery(
    [ENDPOINT.ILLUSTRATION_THEME.GET_LIST],
    () => IllustrationThemeService.getList(),
  );

  const methods = useForm<any>({
    mode: 'onChange',
    resolver: yupResolver(formSchemaFn(!!currentId)),
    reValidateMode: 'onChange',
  });

  const { handleSubmit, reset, setError } = methods;

  const handleCancel = () => {
    setOpenDrawer(false);
    setCurrentId(null);
    setDetail(undefined);
    reset({});
  };

  const { mutate: createTheme, isLoading: isLoadingCreate } = useMutation(
    (body: ICreateIllustrationThemeRequest) =>
      IllustrationThemeService.create(body),
    {
      onSuccess: () => {
        notificationMessage({
          message: 'イラストテーマが正常に作成されました。',
          type: NOTIFY_STATUS.SUCCESS,
        });
        refetch();
        handleCancel();
      },
      onError: (error: IResponseError) => {
        handleErrorCommon(error, setError);
      },
    },
  );

  const { mutate: updateTheme, isLoading: isLoadingUpdate } = useMutation(
    (payload: { body: IUpdateIllustrationThemeRequest; id: number }) =>
      IllustrationThemeService.update(payload.body, payload.id),
    {
      onSuccess: () => {
        notificationMessage({
          message: 'イラストテーマが正常に更新されました。',
          type: NOTIFY_STATUS.SUCCESS,
        });
        refetch();
        handleCancel();
      },
      onError: (error: IResponseError) => {
        handleErrorCommon(error, setError);
      },
    },
  );

  const { mutate: deleteTheme, isLoading: isLoadingDelete } = useMutation(
    (id: number) => IllustrationThemeService.delete(id),
    {
      onSuccess: () => {
        notificationMessage({
          message: 'イラストテーマが正常に削除されました。',
          type: NOTIFY_STATUS.SUCCESS,
        });
        refetch();
        handleCancel();
      },
      onError: (error: IResponseError) => {
        handleErrorCommon(error, setError);
      },
    },
  );

  const { isFetching: isFetchingDetail } = useQuery(
    [ENDPOINT.ILLUSTRATION_THEME.GET_DETAIL.replace(':id', String(currentId))],
    () => IllustrationThemeService.getDetail(currentId!),
    {
      enabled: !!currentId,
      onSuccess: (
        response: IResponse<IGetDetailIllustrationThemeResponse>,
      ) => {
        const data = response?.data;
        setDetail(data);
        reset({
          code: data?.code,
          labelJp: data?.labelJp,
          prompt: data?.prompt,
          previewImageUrl: data?.previewImageUrl ?? '',
          referenceImageUrl: data?.referenceImageUrl ?? '',
          referenceCharacterIds: data?.referenceCharacterIds ?? [],
          referenceCharacterPrompt: data?.referenceCharacterPrompt ?? '',
          sortOrder: data?.sortOrder,
          isActive: data?.isActive,
          coverImage: null,
        });
      },
      onError: (error: IResponseError) => {
        handleErrorCommon(error, setError);
      },
    },
  );

  const onSubmit = async (values: FormValues) => {
    const { coverImage, ...rest } = values;
    try {
      setIsSubmitting(true);
      let previewImageUrl = rest.previewImageUrl;
      if (coverImage?.file) {
        const res = await ImageService.uploadFile(coverImage.file);
        previewImageUrl = res?.data?.url;
      }

      if (currentId) {
        const { code, ...updateBody } = rest;
        updateTheme({
          body: {
            ...updateBody,
            previewImageUrl,
            // 空文字は @IsUrl で弾かれるため、解除は null で送る
            referenceImageUrl: rest.referenceImageUrl || null,
            // 全解除は null で送る（サーバー側でも空配列は NULL 保存）
            referenceCharacterIds: rest.referenceCharacterIds?.length
              ? rest.referenceCharacterIds
              : null,
            // 空欄は上書き解除（プロンプト管理の共通指示に戻す）として null で送る
            referenceCharacterPrompt: rest.referenceCharacterPrompt?.trim()
              ? rest.referenceCharacterPrompt
              : null,
          },
          id: currentId,
        });
      } else {
        createTheme({
          ...rest,
          previewImageUrl,
          // 未設定時はフィールド自体を送らない
          ...(rest.referenceImageUrl
            ? { referenceImageUrl: rest.referenceImageUrl }
            : { referenceImageUrl: undefined }),
          ...(rest.referenceCharacterIds?.length
            ? { referenceCharacterIds: rest.referenceCharacterIds }
            : { referenceCharacterIds: undefined }),
          ...(rest.referenceCharacterPrompt?.trim()
            ? { referenceCharacterPrompt: rest.referenceCharacterPrompt }
            : { referenceCharacterPrompt: undefined }),
        });
      }
    } catch (error) {
      notificationMessage({
        message: 'サムネイル画像のアップロードに失敗しました。',
        type: NOTIFY_STATUS.ERROR,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = () => {
    notificationModal({
      type: NOTIFY_STATUS.CONFIRM,
      title: '削除しますか？',
      maskClosable: false,
      onOk: () => deleteTheme(detail!.id),
      okText: '削除する',
      cancelText: 'キャンセル',
      wrapClassName: 'delete-modal-wrapper',
    });
  };

  const columns: TableColumnsType<any> = [
    {
      title: 'サムネイル',
      dataIndex: 'previewImageUrl',
      align: 'center',
      width: '15%',
      render: (value) =>
        value ? (
          <Image
            width={48}
            height={48}
            src={value}
            preview={false}
            style={{ objectFit: 'cover', borderRadius: 4 }}
          />
        ) : (
          <span className='text-gray-400'>なし</span>
        ),
    },
    {
      title: 'コード',
      dataIndex: 'code',
      align: 'left',
      width: '20%',
      render: (value) => value,
    },
    {
      title: '表示名',
      dataIndex: 'labelJp',
      align: 'left',
      width: '20%',
      render: (value) => value,
    },
    {
      title: '合成キャラ',
      dataIndex: 'referenceImageUrl',
      align: 'center',
      width: '12%',
      // キャラ候補はアップロード画像より優先されるため先に判定する
      render: (value, record) =>
        record.referenceCharacterIds?.length ? (
          <span>{record.referenceCharacterIds.length}体からランダム</span>
        ) : value ? (
          <Image
            width={48}
            height={48}
            src={value}
            preview={false}
            style={{ objectFit: 'cover', borderRadius: 4 }}
          />
        ) : (
          <span className='text-gray-400'>なし</span>
        ),
    },
    {
      title: '利用回数',
      dataIndex: 'usageCount',
      align: 'center',
      width: '10%',
      render: (value) => (value ?? 0).toLocaleString(),
    },
    {
      title: '有効',
      dataIndex: 'isActive',
      align: 'center',
      width: '15%',
      render: (value) =>
        value ? <Tag color='green'>有効</Tag> : <Tag color='red'>無効</Tag>,
    },
    {
      title: '並び順',
      dataIndex: 'sortOrder',
      align: 'center',
      width: '10%',
      render: (value) => value,
    },
    {
      title: '更新日時',
      dataIndex: 'updatedAt',
      align: 'center',
      width: '20%',
      render: (value) => value && moment(value).format('YYYY-MM-DD HH:mm:ss'),
    },
  ];

  return (
    <div className='p-post-list'>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <div className='flex flex-col gap-[16px] mx-auto w-full max-w-[1280px]'>
        <PreviewSamplePanel />
        <div className='flex justify-end'>
          <Button
            type='primary'
            onClick={() => {
              setOpenDrawer(true);
              reset({
                isActive: true,
                sortOrder: 0,
                previewImageUrl: '',
                referenceImageUrl: '',
                referenceCharacterIds: [],
                referenceCharacterPrompt: '',
              });
            }}
          >
            新規作成
          </Button>
        </div>
        <Table
          loading={isFetching}
          scroll={{ x: 800 }}
          bordered
          rowKey='id'
          columns={columns}
          dataSource={data?.data}
          pagination={false}
          onRow={(record) => ({
            onClick: () => {
              setOpenDrawer(true);
              setCurrentId(record.id);
            },
          })}
          locale={{ emptyText: 'イラストテーマはまだありません' }}
        />
      </div>

      <Drawer
        title={!currentId ? 'イラストテーマ新規作成' : 'イラストテーマ詳細'}
        onOk={handleSubmit((values) => onSubmit(values as FormValues))}
        onCancel={handleCancel}
        onDelete={handleDelete}
        maskClosable={false}
        open={openDrawer}
        okText={!currentId ? '保存' : '更新'}
        loading={
          currentId
            ? isFetchingDetail ||
              isLoadingUpdate ||
              isLoadingDelete ||
              isSubmitting
            : isLoadingCreate || isSubmitting
        }
        isDeleted={!!currentId}
      >
        <FormProvider {...methods}>
          <IllustrationThemeForm
            isLoading={isFetchingDetail}
            isEdit={!!currentId}
          />
        </FormProvider>
      </Drawer>
    </div>
  );
};
